import type { Id } from 'convex/_generated/dataModel';
import type {
  ClimateRegion,
  Garden,
  Hemisphere,
  Plant,
  PlantSowingConfig,
  SowingByRegion,
  SowingConfigField,
  SowingConfigFieldValue,
  SowingConfigValues,
  SowingDefaultValues,
  UserPlantDefault,
  UserPreferences,
} from './models';

export type {
  ClimateRegion,
  Garden,
  Hemisphere,
  Plant,
  PlantSowingConfig,
  SowingConfigField,
  SowingConfigFieldValue,
  SowingConfigValues,
  SowingDefaultValues,
  UserPlantDefault,
  UserPreferences,
} from './models';

const DEFAULT_REGION: ClimateRegion = 'temperate';
const DEFAULT_SEED_BUFFER = 1;
const DEFAULT_SOW_WEEK_OF_MONTH = 1;

export function resolvePreferences(prefs: UserPreferences | null | undefined): {
  hemisphere: Hemisphere;
  region: ClimateRegion;
} {
  return {
    hemisphere: prefs?.hemisphere ?? 'southern',
    region: prefs?.region ?? DEFAULT_REGION,
  };
}

export function resolveSowMonths(
  sowMonthsSouthern: SowingByRegion | undefined,
  sowMonthsNorthern: SowingByRegion | undefined,
  hemisphere: Hemisphere,
  region: ClimateRegion
): number[] {
  const byHemisphere = hemisphere === 'northern' ? sowMonthsNorthern : sowMonthsSouthern;
  if (!byHemisphere) return [];
  return byHemisphere[region] ?? byHemisphere.temperate ?? [];
}

/** First catalog sow month + 1st week — used when the user has not set timing. */
export function defaultSowTimingFromCatalog(
  plant: Plant | undefined,
  hemisphere: Hemisphere,
  region: ClimateRegion
): Pick<SowingDefaultValues, 'sowMonth' | 'sowWeekOfMonth'> {
  if (!plant) return {};
  const months = resolveSowMonths(
    plant.sowMonthsSouthern,
    plant.sowMonthsNorthern,
    hemisphere,
    region
  );
  if (months.length === 0) return {};
  return {
    sowMonth: Math.min(...months),
    sowWeekOfMonth: DEFAULT_SOW_WEEK_OF_MONTH,
  };
}

export function getUniquePlantIds(garden: Garden): string[] {
  return [...new Set(garden.plants.map((p) => p.plantId))];
}

export function getPlantsInGarden(garden: Garden | null, allPlants: Plant[]): Plant[] {
  if (!garden) return [];
  const uniqueIds = getUniquePlantIds(garden);

  const speciesIdSet = new Set<string>();
  for (const id of uniqueIds) {
    const plant = allPlants.find((p) => p.id === id);
    speciesIdSet.add(plant?.speciesId ?? id);
  }

  return allPlants.filter(
    (p) => speciesIdSet.has(p.id) && (p.speciesId === undefined || p.speciesId === p.id)
  );
}

/** Virtual default when no plantSowingConfig row exists yet. */
export function defaultSowingIsActive(category?: string): boolean {
  return category !== 'Fruits';
}

function pickOptionalSowTiming(source: {
  sowMonth?: number;
  sowWeekOfMonth?: number;
}): Pick<SowingDefaultValues, 'sowMonth' | 'sowWeekOfMonth'> {
  return {
    ...(source.sowMonth !== undefined ? { sowMonth: source.sowMonth } : {}),
    ...(source.sowWeekOfMonth !== undefined ? { sowWeekOfMonth: source.sowWeekOfMonth } : {}),
  };
}

function withCatalogSowTimingFallback(
  resolved: SowingDefaultValues,
  catalog: Pick<SowingDefaultValues, 'sowMonth' | 'sowWeekOfMonth'>
): SowingDefaultValues {
  const sowMonth = resolved.sowMonth ?? catalog.sowMonth;
  const sowWeekOfMonth =
    resolved.sowWeekOfMonth ??
    (sowMonth !== undefined ? (catalog.sowWeekOfMonth ?? DEFAULT_SOW_WEEK_OF_MONTH) : undefined);

  return {
    seedBuffer: resolved.seedBuffer,
    isActive: resolved.isActive,
    ...(sowMonth !== undefined ? { sowMonth } : {}),
    ...(sowWeekOfMonth !== undefined ? { sowWeekOfMonth } : {}),
  };
}

/**
 * Cascade: garden row → remembered user default → hardcoded virtual.
 * Catalog timing is applied separately via withCatalogSowTimingFallback.
 */
export function resolveSowingConfigValues(
  existing:
    | Pick<PlantSowingConfig, 'seedBuffer' | 'isActive' | 'sowMonth' | 'sowWeekOfMonth'>
    | undefined,
  remembered: SowingDefaultValues | undefined,
  category?: string
): SowingDefaultValues {
  if (existing) {
    return {
      seedBuffer: existing.seedBuffer,
      isActive: existing.isActive ?? defaultSowingIsActive(category),
      ...pickOptionalSowTiming(existing),
    };
  }
  if (remembered) {
    return {
      seedBuffer: remembered.seedBuffer,
      isActive: remembered.isActive,
      ...pickOptionalSowTiming(remembered),
    };
  }
  return {
    seedBuffer: DEFAULT_SEED_BUFFER,
    isActive: defaultSowingIsActive(category),
  };
}

export function getRememberedSowingDefault(
  defaults: UserPlantDefault[],
  plantId: string
): SowingDefaultValues | undefined {
  const row = defaults.find((d) => d.plantId === plantId);
  return row?.sowing;
}

export function getSowingConfigForPlant(
  configs: PlantSowingConfig[],
  gardenId: Id<'gardens'>,
  plantId: string,
  category?: string,
  defaults: UserPlantDefault[] = [],
  plant?: Plant,
  preferences?: UserPreferences | null
): SowingConfigValues {
  const existing = configs.find((c) => c.plantId === plantId);
  const remembered = getRememberedSowingDefault(defaults, plantId);
  const resolved = resolveSowingConfigValues(existing, remembered, category);
  const { hemisphere, region } = resolvePreferences(preferences);
  const withTiming = withCatalogSowTimingFallback(
    resolved,
    defaultSowTimingFromCatalog(plant, hemisphere, region)
  );

  return {
    gardenId: existing?.gardenId ?? gardenId,
    plantId,
    seedBuffer: withTiming.seedBuffer,
    isActive: withTiming.isActive,
    ...pickOptionalSowTiming(withTiming),
  };
}

export function parseSowSelectValue(value: string): number | undefined {
  if (value === '') return undefined;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function isValidSowMonth(month: number | undefined): boolean {
  return month === undefined || (month >= 1 && month <= 12);
}

export function isValidSowWeekOfMonth(week: number | undefined): boolean {
  return week === undefined || (week >= 1 && week <= 4);
}

type UpdatePlantSowingConfigFn = (args: {
  gardenId: Id<'gardens'>;
  plantId: string;
  seedBuffer: number;
  isActive: boolean;
  sowMonth?: number;
  sowWeekOfMonth?: number;
}) => Promise<unknown>;

function toUpsertArgs(
  gardenId: Id<'gardens'>,
  plantId: string,
  config: SowingDefaultValues
): Parameters<UpdatePlantSowingConfigFn>[0] {
  return {
    gardenId,
    plantId,
    seedBuffer: config.seedBuffer,
    isActive: config.isActive,
    ...pickOptionalSowTiming(config),
  };
}

function findPlant(plants: Plant[], plantId: string): Plant | undefined {
  return plants.find((p) => p.id === plantId);
}

export function createHandleUpdateSowingConfig(
  selectedGarden: Garden | null,
  storeConfigs: PlantSowingConfig[],
  updatePlantSowingConfig: UpdatePlantSowingConfigFn,
  defaults: UserPlantDefault[] = [],
  plants: Plant[] = [],
  preferences?: UserPreferences | null
) {
  return async (plantId: string, field: SowingConfigField, value: SowingConfigFieldValue) => {
    if (!selectedGarden) return;
    const plant = findPlant(plants, plantId);
    const currentConfig = getSowingConfigForPlant(
      storeConfigs,
      selectedGarden._id,
      plantId,
      plant?.category,
      defaults,
      plant,
      preferences
    );

    const updatedConfig: SowingDefaultValues = {
      seedBuffer: currentConfig.seedBuffer,
      isActive: currentConfig.isActive,
      ...pickOptionalSowTiming(currentConfig),
    };

    if (field === 'seedBuffer' && typeof value === 'number') {
      updatedConfig.seedBuffer = value;
    } else if (field === 'isActive' && typeof value === 'boolean') {
      updatedConfig.isActive = value;
    } else if (field === 'sowMonth') {
      if (value === undefined) delete updatedConfig.sowMonth;
      else if (typeof value === 'number') updatedConfig.sowMonth = value;
      // Changing/clearing month without an explicit week keeps 1st week when a month remains
      if (updatedConfig.sowMonth !== undefined && updatedConfig.sowWeekOfMonth === undefined) {
        updatedConfig.sowWeekOfMonth = DEFAULT_SOW_WEEK_OF_MONTH;
      }
    } else if (field === 'sowWeekOfMonth') {
      if (value === undefined) delete updatedConfig.sowWeekOfMonth;
      else if (typeof value === 'number') updatedConfig.sowWeekOfMonth = value;
    }

    if (!isValidSowMonth(updatedConfig.sowMonth)) return;
    if (!isValidSowWeekOfMonth(updatedConfig.sowWeekOfMonth)) return;

    await updatePlantSowingConfig(toUpsertArgs(selectedGarden._id, plantId, updatedConfig));
  };
}

export function createHandleBulkApplySowingConfig(
  selectedGarden: Garden | null,
  selectedPlantIds: Set<string>,
  storeConfigs: PlantSowingConfig[],
  updatePlantSowingConfig: UpdatePlantSowingConfigFn,
  clearSelection: () => void,
  defaults: UserPlantDefault[] = [],
  plants: Plant[] = [],
  preferences?: UserPreferences | null
) {
  return async (seedBuffer: number, isActive: boolean) => {
    if (!selectedGarden) return;
    for (const plantId of Array.from(selectedPlantIds)) {
      const plant = findPlant(plants, plantId);
      const current = getSowingConfigForPlant(
        storeConfigs,
        selectedGarden._id,
        plantId,
        plant?.category,
        defaults,
        plant,
        preferences
      );
      await updatePlantSowingConfig(
        toUpsertArgs(selectedGarden._id, plantId, {
          seedBuffer,
          isActive,
          ...pickOptionalSowTiming(current),
        })
      );
    }
    clearSelection();
  };
}
